import { memo, useCallback } from 'react';
import type { FC } from 'react';
import { type CampaignSource, type CampaignTypes, useCampaignActions } from 'features/campaigns';
import { campaignMapper } from 'features/campaigns/services/campaignCardService';
import CampaignCard from '../CampaignCard/CampaignCard';
import { useScrollDetails } from 'layouts/Section/hooks';
import { LeftArrow, RightArrow } from '../../../../../layouts/Section/ScrollArrows';
import { setViewMode, setSelectedSection } from 'store/app/appStore';;
import type { SECTION_NAMES } from '@repo/common';
import { SINGLE_VIEW_MODE } from '../../../../../constants';
import styles from './MultiCampaignGrid.module.css';

export interface MultiCampaignsGridProps {
  name: typeof SECTION_NAMES[keyof typeof SECTION_NAMES];
  campaigns: CampaignSource[];
  /** Whether the section is currently active */
  isActive?: boolean;
}

const MultiCampaignsGrid: FC<MultiCampaignsGridProps> = ({ name, campaigns, isActive = false }) => {
  const { setSelectedCampaignId } = useCampaignActions();

  const { gridContainerRef, scrollArrows, scrollLeft, scrollRight, handleMouseEnter, handleMouseLeave } =
    useScrollDetails(isActive, name, campaigns.length);

  // Scroll to second card in portrait view when component loads
  // 
  // This is deprrecated at the moment, but at some point if we have real caurusel functionality 
  // we may want to re-enable this to ensure the second card is centered on load in portrait view
  // useEffect(() => {
  //   if (isPortrait && gridContainerRef.current) {
  //     scrollToSecondCard(gridContainerRef.current);
  //   }
  // }, [isPortrait, gridContainerRef]);

  const handleCampaignCardClick = useCallback(
    (campaign: CampaignSource) => {
      setSelectedCampaignId(campaign.id, campaign.type);
      setSelectedSection(name);
      setViewMode(SINGLE_VIEW_MODE);
    },
    [name, setSelectedCampaignId],
  );

  return (
    <div
      className={styles.scrollableCampaignGridWrapper}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div
        className={styles.multiCampaignGridContainer}
        ref={gridContainerRef}
      >
        {campaigns.map((campaign, index) => {
          const mappedCampaign = campaignMapper(campaign.type as CampaignTypes, [campaign]);

          if (!mappedCampaign) return null;

          return (
            <div
              key={index}
              onClick={() => handleCampaignCardClick(campaign)}
              className={styles.multiCampaignGridCardWrapper}
            >
              <CampaignCard campaignInfo={{ ...mappedCampaign.campaignInfo, campaignId: campaign.id }} />
            </div>
          );
        })}
      </div>

      {scrollArrows.isHovered && (
        <>
          {scrollArrows.showLeftArrow && <LeftArrow onClick={scrollLeft} />}
          {scrollArrows.showRightArrow && <RightArrow onClick={scrollRight} />}
        </>
      )}
    </div>
  );
};

export default memo(MultiCampaignsGrid);
